<template lang="pug">
.flex.flex-row.mb-4
  h1.mr-4 {{ $t("materialUse") }}
  el-button(v-if="isTicketInProcess", icon="Plus", @click="showAddArticle") {{ $t("addMaterial") }}

.overflow-x-scroll.max-w-full
  el-table(
    :data="consumptionRelations",
    fit,
    table-layout="auto",
    style="width: 100%"
  )
    el-table-column(:label="$t('name')")
      template(#default="scope")
        TwinField(:id="scope.row.toUuid")
    el-table-column(
      :label="$t('amount')",
      prop="attributes.amount",
      :min-width="100",
      align="center"
    )
    el-table-column(
      v-if="isTicketInProcess",
      :label="$t('actions')",
      :min-width="100",
      align="center"
    )
      template(#default="scope")
        el-popconfirm(
          width="300",
          :title="$t('confirmDelete')",
          @confirm="() => deleteArticle(scope.row.uuid)",
          :confirm-button-text="$t('remove')",
          confirm-button-type="danger",
          :cancel-button-text="$t('cancel')"
        )
          template(#reference)
            .pr-1.pl-1.cursor-pointer.delete-material
              el-icon
                Delete 

el-dialog(
  v-model="addArticleVisible",
  :title="$t('addMaterial')",
  :width="selectedArticle === null ? '100%' : '700px'"
)
  .article-dialog-content
    smx-form(width="100%")
      smx-form-item(:label="$t('articles')")
        template(v-if="selectedArticle === null")
          .flex-col
            .w-96.mb-4
              QRInput(
                v-model="search",
                suffix-icon="search",
                @keyup.enter="() => (instantSearch = search)"
              )
            TwinTableDataLoading#wh-nomenclature(
              :query="{ types: [whSingleArticles, whArticlesMultiple], active: true }",
              :template-query="{ type: whArticlesPrefix }",
              :search="search",
              :hide-delete="true",
              :instant-search="instantSearch",
              @row-click="onRowClick"
            )
        .flex.flex-row.items-center(v-else)
          TwinField(:twin="selectedArticle")
          .cursor-pointer.ml-4(
            :style="{ color: 'var(--el-color-primary)' }",
            @click="() => (selectedArticle = null)"
          ) {{ $t("chooseDifferentArticle") }}
      smx-form-item(
        v-if="selectedArticle?.type === 'wh.article.m'",
        :label="$t('amount')"
      )
        .flex.items-center
          el-input-number.mr-4.material-amount-input(
            v-model="amount",
            :min="0",
            :max="isRemoveArticleFromWarehouse ? maximumAmount : undefined"
          )
          span(v-loading="articleDetailsLoadState.loading") {{ $t("itemsAvailable", { items: maximumAmount }) }}

      smx-form-item(v-if="selectedArticle !== null")
        el-switch(
          v-model="isRemoveArticleFromWarehouse",
          :active-text="$t('removeArticleFromWarehouse')"
        )

  template(#footer)
    el-button(type="danger", @click="cancel") {{ $t("cancel") }}
    el-button(
      v-loading="saveLoading",
      type="primary",
      :disabled="!saveArticleAvailable",
      @click="addArticle"
    ) {{ $t("save") }}
</template>

<script setup lang="ts">
import {
  type LoadState,
  stateError,
  stateInitial,
  stateLoaded,
  stateLoading,
} from "@shared/models/state";
import { getTwin, type TwinResponse } from "@shared/services/twinManagement";
import { watch } from "vue";
import { computed } from "vue";
import { ref } from "vue";
import { materialConsumptionId } from "../constants";
import { useTwinStore } from "@shared/stores/twinManagement";
import TwinField from "@shared/components/twinManagement/components/TwinField.vue";
import TwinTableDataLoading from "@shared/components/twinManagement/components/TwinTableDataLoading.vue";
import {
  whArticlesPrefix,
  whSingleArticles,
  whArticlesMultiple,
} from "@shared/warehouse/constants";
import QRInput from "@shared/components/elements/QRInput.vue";
import { postOutbound } from "@shared/services/warehouse";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { statuses, ticketProps } from "@shared/components/tickets/constants";

const props = defineProps<{ ticket: TwinResponse }>();

const twinManagementStore = useTwinStore();

const consumptionRelations = computed(() => {
  return twinManagementStore.relationsLoadingState.result?.filter(
    (relation) => relation.contextId === materialConsumptionId,
  );
});

const isTicketInProcess = computed(
  () =>
    props.ticket.properties?.find((p) => p.customId === ticketProps.status)
      ?.propertyValue?.value === statuses.inProcess,
);

const selectedArticle = ref<TwinResponse | null>(null);

const addArticleVisible = ref(false);

const amount = ref(0);
const isRemoveArticleFromWarehouse = ref(true);

// If article is being removed, check if the amount exceeds the maximum
watch(isRemoveArticleFromWarehouse, () => {
  if (
    isRemoveArticleFromWarehouse.value &&
    amount.value &&
    maximumAmount.value &&
    amount.value > maximumAmount.value
  ) {
    amount.value = maximumAmount.value;
  }
});

let abortController: AbortController | null = null;

const articleDetailsLoadState = ref<LoadState<TwinResponse>>({
  ...stateInitial,
});

const maximumAmount = computed<number>(
  () =>
    articleDetailsLoadState.value?.result?.properties?.find(
      (item) => item.customId === "amount",
    )?.propertyValue?.value as number,
);

async function loadArticleDetails() {
  if (selectedArticle.value === null) {
    return;
  }
  articleDetailsLoadState.value = { ...stateLoading };
  // NOTE: since there is not guarantee how long a request will take, we have to cancel existing requests
  if (abortController !== null) {
    try {
      abortController.abort();
    } catch (e) {
      // noop
    }
    abortController = null;
  }
  abortController = new AbortController();
  const response = await getTwin(selectedArticle.value.uuid, {
    signal: abortController.signal,
  }).catch((e) => {
    if (e && e?.code === "ERR_CANCELED") {
      // We cancelled the request in favor of a new search query
      e.dontLog = true;
      throw e;
    }
    articleDetailsLoadState.value = { ...stateError };
    throw e;
  });
  articleDetailsLoadState.value = { ...stateLoaded, result: response.data };
}

watch(selectedArticle, (value, oldValue) => {
  // If the article is of type m, we have to load it's properties to determine the maximum amount
  if (
    value &&
    selectedArticle.value !== null &&
    selectedArticle.value.type === "wh.article.m" &&
    value !== oldValue
  ) {
    void loadArticleDetails();
  }
});

function showAddArticle() {
  addArticleVisible.value = true;
  selectedArticle.value = null;
  search.value = "";
  amount.value = 0;
  isRemoveArticleFromWarehouse.value = true;
}

function cancel() {
  closeAddArticle();
}

function closeAddArticle() {
  addArticleVisible.value = false;
}

const saveLoading = ref(false);

async function addArticle() {
  if (saveArticleAvailable.value && selectedArticle.value) {
    saveLoading.value = true;
    await twinManagementStore
      .addTwinRelation({
        fromUuid: props.ticket.uuid,
        toUuid: selectedArticle.value.uuid,
        contextId: materialConsumptionId,
        attributes: {
          amount:
            selectedArticle.value.type === "wh.article.m"
              ? amount.value
              : undefined,
        },
      })
      .finally(() => {
        saveLoading.value = false;
      });
    if (isRemoveArticleFromWarehouse.value) {
      await removeArticleFromWarehouse();
    }
    closeAddArticle();
  }
}

const { t } = useI18n();

async function removeArticleFromWarehouse() {
  if (selectedArticle.value === null) {
    return;
  }

  const toUser = (
    props.ticket.properties!.find((p) => p.customId === ticketProps.assignee)
      ?.propertyValue?.value as string[]
  )?.[0];

  await postOutbound({
    uuid: selectedArticle.value.uuid,
    recipient: toUser,
    amount:
      selectedArticle.value.type === "wh.article.m" ? amount.value : undefined,
    comment: "Ausgabe",
    longComment: `Ausgabe an ${toUser}, für Ticket ${props.ticket.name} (${props.ticket.uuid})`,
  }).then(
    () => {},
    () => {
      ElNotification({
        type: "error",
        title: t("error"),
        message: t("errorRemovingArticleFromWarehouse"),
      });
    },
  );
}

const saveArticleAvailable = computed(() => {
  if (
    saveLoading.value ||
    selectedArticle.value === null ||
    selectedArticle.value === undefined
  ) {
    return false;
  }
  if (selectedArticle.value.type === "wh.article.m" && amount.value === 0) {
    return false;
  }
  return true;
});

function deleteArticle(id: string) {
  void twinManagementStore.removeTwinRelation(id);
}

// Material selection
const search = ref("");
const instantSearch = ref("");

// Remove selected article as soon as a new search is triggered
watch(search, () => {
  selectedArticle.value = null;
});

function onRowClick(article: TwinResponse) {
  selectedArticle.value = article;
}
</script>
